import type { EntityView } from '~/features/memory/entity-list';

/**
 * FRONT-876 review fix: the ONE definition of the Memory page's URL-hash
 * mini-router — `parseHash`/`buildHash`/`resolveHashView` used to live only
 * in `routes/memory.tsx`, with the command palette's Memory section
 * hand-copying `buildHash`'s Entities-tab shape into its own local
 * `memoryEntityHref` (the stated reason — avoiding a lazy-route-chunk import
 * — didn't hold, since these are pure functions with no React and no route
 * imports). Nothing pinned the two together, so a change to `buildHash` here
 * could silently break the palette's deep link without a single test going
 * red. Both `memory.tsx` and `command-palette.tsx` now import from here;
 * `memory.tsx` re-exports all three so its own existing imports (and tests)
 * keep working unchanged.
 */

export type Tab = 'entities' | 'graph' | 'activity' | 'reflections' | 'conflicts' | 'trash';

export const VALID_TABS: readonly Tab[] = ['entities', 'graph', 'activity', 'reflections', 'conflicts', 'trash'];

/**
 * Parse `#graph?entity=abc&view=archived` → { tab: 'graph', entityId: 'abc',
 * view: 'archived' }. `view` (FRONT-1155) is only meaningful on the Entities
 * tab, but parsing it unconditionally is harmless — `buildHash` never writes
 * it into another tab's hash, so a real URL never carries it there. Absent
 * `view` always means the Active view, so pre-FRONT-1155 links keep working
 * unchanged.
 */
export function parseHash(hash: string): { tab: Tab; entityId: string | null; view: EntityView } {
  const trimmed = hash.replace(/^#/, '');
  if (!trimmed) return { tab: 'entities', entityId: null, view: 'active' };
  const [tabPart, queryPart] = trimmed.split('?');
  const tab = (VALID_TABS as readonly string[]).includes(tabPart)
    ? (tabPart as Tab)
    : 'entities';
  const params = queryPart ? new URLSearchParams(queryPart) : null;
  const entityId = params?.get('entity') ?? null;
  const view: EntityView = params?.get('view') === 'archived' ? 'archived' : 'active';
  return { tab, entityId, view };
}

export function buildHash(tab: Tab, entityId: string | null, view: EntityView): string {
  const params = new URLSearchParams();
  // Note: URLSearchParams' percent-encoding differs from encodeURIComponent
  // for a space, ~, ', ( and ) — not reachable today since every entity id is
  // a generated UUID, a Zitadel user id, or a legacy slug, and the round trip
  // stays safe regardless because parseHash decodes with URLSearchParams too.
  if (entityId) params.set('entity', entityId);
  // The Active/Inactive toggle only exists on the Entities tab — never leak
  // it into another tab's hash.
  if (tab === 'entities' && view === 'archived') params.set('view', 'archived');
  const qs = params.toString();
  return qs ? `#${tab}?${qs}` : `#${tab}`;
}

/**
 * FRONT-1155 code-review fix: whether the hash→state effect should apply the
 * hash's parsed `view` onto state. The Active/Inactive toggle only ever gets
 * ENCODED into the Entities tab's hash (see `buildHash`), but `parseHash`
 * unconditionally returns a `view` for every hash — defaulting to 'active'
 * when the tab's hash carries none. Reading that default back on every hash
 * change (switching to Graph via "Show in graph", clicking any tab-bar
 * button, browser back/forward) would silently reset the toggle to Active
 * even though the user never touched it. Pulled out as a pure function so
 * the gate itself is unit-testable — the effect it lives in only runs on a
 * real render/navigation, which this test harness (static rendering, no
 * jsdom) can't exercise.
 */
export function resolveHashView(parsedTab: Tab, parsedView: EntityView, currentView: EntityView): EntityView {
  return parsedTab === 'entities' ? parsedView : currentView;
}

/**
 * FRONT-2498: a link into the Reflections tab that opens on ONE proposal
 * (`#reflections?proposal=42`), used by the Reflection page's "Review". Kept
 * apart from `parseHash`/`buildHash`: the id is a one-shot focus, read once
 * on arrival — the page's own hash sync then rewrites the hash without it.
 */
export function reflectionReviewHash(proposalId: number): string {
  return `#reflections?proposal=${proposalId}`;
}

export function parseReflectionFocus(hash: string): number | null {
  const [tabPart, queryPart] = hash.replace(/^#/, '').split('?');
  if (tabPart !== 'reflections' || !queryPart) return null;
  const raw = new URLSearchParams(queryPart).get('proposal');
  if (!raw || !/^\d{1,9}$/.test(raw)) return null;
  return Number(raw);
}
